import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, ArrowLeft, Upload, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const industries = ['Technology', 'Construction', 'Retail', 'Healthcare', 'Education', 'Hospitality', 'Manufacturing', 'Other'];

const commonTags = [
  'B2B', 'B2C', 'E-commerce', 'Local', 'Online', 'Sustainable', 
  'Family-owned', 'Startup', 'Consulting', 'Services', 'Products',
  'Export', 'Import', 'Manufacturing', 'Wholesale', 'Retail'
];

function ProfileEditPage({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [business, setBusiness] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    company_registration_number: '',
    years_experience: '',
    philosophy: '',
    contact_email: '',
    contact_phone: '',
    bbbee_level: '',
    tags: [],
  });
  const [newTag, setNewTag] = useState('');
  const [registrationError, setRegistrationError] = useState('');

  useEffect(() => {
    fetchBusiness();
  }, []);

  const fetchBusiness = async () => {
    try {
      const response = await axios.get(`${API}/businesses`, { withCredentials: true });
      const myBusiness = response.data.find((b) => b.user_id === user.user_id);
      if (myBusiness) {
        setBusiness(myBusiness);
        setFormData({
          name: myBusiness.name,
          industry: myBusiness.industry,
          company_registration_number: myBusiness.company_registration_number || '',
          years_experience: myBusiness.years_experience,
          philosophy: myBusiness.philosophy,
          contact_email: myBusiness.contact_email,
          contact_phone: myBusiness.contact_phone,
          bbbee_level: myBusiness.bbbee_level || '',
          tags: myBusiness.tags || [],
        });
      }
    } catch (error) {
      console.error('Error fetching business:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Validate company registration number format
    if (name === 'company_registration_number') {
      const pattern = /^\d{4}\/\d{6,8}\/\d{2}$/;
      if (value && !pattern.test(value)) {
        setRegistrationError('Format must be: YYYY/NNNNNN/NN (e.g., 2014/983409/07)');
      } else {
        setRegistrationError('');
      }
    }
  };

  const addTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tagToRemove) });
  };

  const handleAddCustomTag = () => {
    if (newTag.trim()) {
      addTag(newTag.trim());
      setNewTag('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (business) {
        await axios.put(`${API}/businesses/${business.business_id}`, formData, { withCredentials: true });
        toast.success('Business updated successfully!');
      } else {
        await axios.post(`${API}/businesses`, formData, { withCredentials: true });
        toast.success('Business created successfully!');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (!business) {
      toast.error('Please create your business profile first');
      return;
    }

    const file = acceptedFiles[0];
    const formData = new FormData();
    formData.append('file', file);
    formData.append('business_id', business.business_id);

    try {
      await axios.post(`${API}/businesses/upload-document`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded successfully!');
    } catch (error) {
      toast.error('Document upload failed');
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, maxFiles: 1 });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="profile-edit-page">
      <div className="container mx-auto px-6 md:px-12 py-12">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6" data-testid="back-button">
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back to Dashboard
        </Button>

        <h1 className="text-4xl font-outfit font-bold text-primary mb-8">
          {business ? 'Edit Business Profile' : 'Create Business Profile'}
        </h1>

        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="business-form">
            <div>
              <Label htmlFor="name">Business Name *</Label>
              <Input
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="mt-2"
                data-testid="name-input"
              />
            </div>

            <div>
              <Label htmlFor="industry">Industry</Label>
              <Select value={formData.industry} onValueChange={(val) => setFormData({ ...formData, industry: val })}>
                <SelectTrigger className="mt-2" data-testid="industry-select">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {industries.map((ind) => (
                    <SelectItem key={ind} value={ind}>
                      {ind}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="company_registration_number">
                Company Registration Number *
              </Label>
              <Input
                id="company_registration_number"
                name="company_registration_number"
                required
                value={formData.company_registration_number}
                onChange={handleChange}
                className="mt-2"
                placeholder="2014/983409/07"
                data-testid="registration-number-input"
              />
              {registrationError && (
                <p className="text-sm text-red-600 mt-1">{registrationError}</p>
              )}
              <p className="text-xs text-slate-500 mt-1">
                Format: YYYY/NNNNNN/NN (e.g., 2014/983409/07). Please upload registration certificate below.
              </p>
            </div>

            <div>
              <Label htmlFor="years_experience">Years of Experience *</Label>
              <Input
                id="years_experience"
                name="years_experience"
                type="number"
                required
                value={formData.years_experience}
                onChange={handleChange}
                className="mt-2"
                min="0"
                data-testid="years-input"
              />
            </div>

            <div>
              <Label htmlFor="philosophy">Business Philosophy *</Label>
              <Textarea
                id="philosophy"
                name="philosophy"
                required
                value={formData.philosophy}
                onChange={handleChange}
                rows={4}
                className="mt-2"
                placeholder="Describe your business philosophy and values"
                data-testid="philosophy-input"
              />
            </div>

            <div>
              <Label htmlFor="contact_email">Business Email Address *</Label>
              <Input
                id="contact_email"
                name="contact_email"
                type="email"
                required
                value={formData.contact_email}
                onChange={handleChange}
                className="mt-2"
                data-testid="contact-email-input"
              />
            </div>

            <div>
              <Label htmlFor="contact_phone">Business Number *</Label>
              <Input
                id="contact_phone"
                name="contact_phone"
                required
                value={formData.contact_phone}
                onChange={handleChange}
                className="mt-2"
                placeholder="+27 11 234 5678"
                data-testid="contact-phone-input"
              />
            </div>

            <div>
              <Label htmlFor="bbbee_level">BBBEE Level</Label>
              <Select 
                value={formData.bbbee_level ? formData.bbbee_level.toString() : 'none'} 
                onValueChange={(val) => setFormData({ ...formData, bbbee_level: val === 'none' ? null : parseInt(val) })}
              >
                <SelectTrigger className="mt-2" data-testid="bbbee-select">
                  <SelectValue placeholder="Select BBBEE level (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="1">Level 1</SelectItem>
                  <SelectItem value="2">Level 2</SelectItem>
                  <SelectItem value="3">Level 3</SelectItem>
                  <SelectItem value="4">Level 4</SelectItem>
                  <SelectItem value="5">Level 5</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Please upload BBBEE certificate below if applicable.
              </p>
            </div>

            {/* Tags Section */}
            <div>
              <Label>Business Tags</Label>
              <p className="text-sm text-slate-600 mb-3">Add tags to help customers find your business</p>
              
              {/* Selected Tags */}
              <div className="flex flex-wrap gap-2 mb-3" data-testid="selected-tags">
                {formData.tags.map((tag) => (
                  <Badge 
                    key={tag} 
                    variant="secondary"
                    className="bg-accent-orange/10 text-accent-orange hover:bg-accent-orange/20 px-3 py-1"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-2 hover:text-accent-orange/70"
                      data-testid={`remove-tag-${tag}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>

              {/* Common Tags */}
              <div className="mb-3">
                <p className="text-sm font-medium text-slate-700 mb-2">Quick Add:</p>
                <div className="flex flex-wrap gap-2">
                  {commonTags.filter(tag => !formData.tags.includes(tag)).slice(0, 10).map((tag) => (
                    <Button
                      key={tag}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addTag(tag)}
                      className="border-slate-300 hover:border-accent-orange hover:text-accent-orange"
                      data-testid={`add-tag-${tag}`}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {tag}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Custom Tag Input */}
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomTag())}
                  placeholder="Add custom tag..."
                  className="flex-1"
                  data-testid="custom-tag-input"
                />
                <Button
                  type="button"
                  onClick={handleAddCustomTag}
                  variant="outline"
                  data-testid="add-custom-tag-button"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-accent-orange text-white hover:bg-[#D97000] rounded-full py-6"
              data-testid="submit-button"
            >
              {loading ? 'Saving...' : business ? 'Update Business' : 'Create Business'}
            </Button>
          </form>

          {business && (
            <div className="mt-8 pt-8 border-t border-slate-200">
              <h2 className="text-xl font-outfit font-semibold text-primary mb-4">
                Upload Registration & BBBEE Certificates
              </h2>
              <p className="text-sm text-slate-600 mb-4">
                Upload your company registration certificate and BBBEE certificate (if applicable)
              </p>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer ${
                  isDragActive ? 'border-accent-orange bg-accent-orange/5' : 'border-slate-300'
                }`}
                data-testid="document-upload"
              >
                <input {...getInputProps()} />
                <Upload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600">
                  {isDragActive ? 'Drop the file here' : 'Drag and drop a document, or click to select'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileEditPage;
